import type { PaletteOptions, Theme, TypographyVariantsOptions } from '@mui/material/styles';

import { TINT_SCALES, type TintScale } from './looks';

const NEUTRAL_STEPS = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900] as const;

/** Returns the CSS colour of a `tokens.css` variable, with an optional alpha. */
export function token(name: string, alpha?: number): string {
  return alpha === undefined ? `rgb(var(--color-${name}))` : `rgb(var(--color-${name}) / ${alpha})`;
}

function intent(scale: string): PaletteOptions['primary'] {
  return {
    main: token(`${scale}-600`),
    light: token(`${scale}-400`),
    dark: token(`${scale}-700`),
    contrastText: token('on-filled'),
  };
}

/** Returns the tint roles of every scale: light steps in the light scheme, dark steps in the dark one. */
function tints(dark: boolean): Record<TintScale, { fill: string; text: string; border: string; hover: string }> {
  const [fill, text, border, hover] = dark ? [900, 100, 700, 800] : [50, 800, 200, 100];
  return Object.fromEntries(
    TINT_SCALES.map((scale) => [
      scale,
      {
        fill: token(`${scale}-${fill}`),
        text: token(`${scale}-${text}`),
        border: token(`${scale}-${border}`),
        hover: token(`${scale}-${hover}`),
      },
    ]),
  ) as Record<TintScale, { fill: string; text: string; border: string; hover: string }>;
}

/** Returns the text, fill and border of one tint, for either scheme. */
export function tinted(theme: Theme, scale: TintScale) {
  const role = theme.vars.palette.tint[scale];
  return { color: role.text, backgroundColor: role.fill, borderColor: role.border };
}

/** The light palette. The dark palette differs in `outline`, the tints and the brand text. */
const LIGHT: PaletteOptions = {
  primary: intent('brand'),
  secondary: intent('accent'),
  error: intent('danger'),
  success: intent('success'),
  info: intent('brand'),
  warning: {
    main: token('warning-400'),
    light: token('warning-200'),
    dark: token('warning-600'),
    contrastText: token('warning-900'),
  },
  grey: Object.fromEntries(NEUTRAL_STEPS.map((step) => [step, token(`neutral-${step}`)])),
  text: {
    heading: token('neutral-900'),
    brand: token('brand-600'),
    primary: token('neutral-800'),
    secondary: token('neutral-500'),
    disabled: token('neutral-400'),
  },
  background: {
    default: token('neutral-50'),
    paper: token('surface'),
  },
  divider: token('neutral-200'),
  outline: token('neutral-300'),
  tint: tints(false),
  action: {
    active: token('neutral-500'),
    hover: token('raised'),
    selected: token('raised'),
    focus: token('neutral-900', 0.1),
    disabled: token('neutral-400'),
    disabledBackground: token('neutral-900', 0.08),
    hoverOpacity: 0.08,
    focusOpacity: 0.1,
    selectedOpacity: 0.12,
    activatedOpacity: 0.1,
  },
};

/** The palettes of the two colour schemes. Most tokens change under `.dark` by themselves. */
export const PALETTES: Record<'light' | 'dark', PaletteOptions> = {
  light: LIGHT,
  dark: {
    ...LIGHT,
    outline: token('neutral-400'),
    tint: tints(true),
    text: { ...LIGHT.text, brand: token('brand-300') },
  },
};

/** The font weights. `medium` and `black` come from `tokens.css`. */
export const WEIGHT = {
  regular: 400,
  medium: 'var(--font-weight-medium)',
  bold: 700,
  black: 'var(--font-weight-black)',
} as const;

/** The kit type sizes outside the Material 3 scale, from `tokens.css`. */
export const TEXT_SIZE = { control: 'var(--text-control)' } as const;

function step(fontSize: number, lineHeight: number, fontWeight: number = WEIGHT.regular) {
  return { fontSize: `${fontSize}px`, lineHeight: `${lineHeight}px`, fontWeight };
}

/** The Material 3 type scale in Lato, mapped onto the MUI variants. */
export const TYPOGRAPHY: TypographyVariantsOptions = {
  fontFamily: 'var(--font-sans)',
  h1: step(36, 44),
  h2: step(32, 40),
  h3: step(28, 36),
  h4: step(24, 32, WEIGHT.bold),
  h5: step(22, 28),
  h6: step(16, 24, WEIGHT.bold),
  subtitle1: step(16, 24, WEIGHT.bold),
  subtitle2: step(14, 20, WEIGHT.bold),
  body1: step(14, 20),
  body2: step(12, 16),
  button: { ...step(14, 20, WEIGHT.bold), textTransform: 'none' },
  caption: step(12, 16),
  overline: { ...step(11, 16, WEIGHT.bold), letterSpacing: '0.05em' },
  label: step(12, 16, WEIGHT.bold),
  meta: step(11, 16),
  tag: { ...step(10, 14, WEIGHT.bold), textTransform: 'uppercase', letterSpacing: '0.08em' },
  metaStrong: step(11, 16, WEIGHT.bold),
  micro: step(9, 12),
  specimenSmall: step(18, 28),
  specimen: step(24, 32),
  specimenLarge: step(36, 44),
};

/** The Material 3 corner radii in px, and the kit radii from `tokens.css`. */
export const SHAPE = {
  hairline: 'var(--radius-hairline)',
  extraSmall: 4,
  compact: 'var(--radius-compact)',
  small: 8,
  medium: 12,
  large: 16,
  extraLarge: 28,
  full: 999,
} as const;

/** The `tokens.css` drop shadow of each raised step. */
export const ELEVATION = {
  sm: 'var(--elevation-sm)',
  md: 'var(--elevation-md)',
  lg: 'var(--elevation-lg)',
  xl: 'var(--elevation-xl)',
} as const;
